import { Suspense, useMemo, lazy } from 'react';
import { wrapCreateBrowserRouterV6, wrapCreateMemoryRouterV6 } from '@sentry/react';
import {
  RouterProvider,
  createHashRouter,
  createMemoryRouter,
  Navigate,
  Outlet,
} from 'react-router-dom';
import ProtectedRoute from './ProtectedRoute';
import RouteError from '../error/RouteError';
import { UiLoader } from '@/ui';
import { AccessDeniedRoute, NotFoundRoute } from '../features/system/routes';
import { getMountConfig } from '../mountConfig';
import { AppLayout } from '@/layout/AppLayout';
import { AuthGate } from '@/auth';
import { CaseManagementStyleProvider } from '../features/caseManagement/CaseManagementStyleProvider';
import NewCaseSession from '../features/caseManagement/NewCaseSession';
import OrderStatusTrackerRedirect from './OrderStatusTrackerRedirect';

// Sentry-instrumented router factories — name performance transactions by
// route pattern. Fall through to the plain factory when Sentry is not
// initialized (stub mode / no DSN).
const sentryCreateHashRouter = wrapCreateBrowserRouterV6(createHashRouter);
const sentryCreateMemoryRouter = wrapCreateMemoryRouterV6(createMemoryRouter);

const LoginPage = lazy(() => import('../features/auth/LoginPage'));
const ReportsOverviewRoute = lazy(() => import('../features/reports/routes'));
const VendorManagementRoute = lazy(() => import('../features/vendorManagement/routes'));
const ClientListRoute = lazy(() => import('../features/admin/clients/ClientListRoute'));
const AddClientRoute = lazy(() => import('../features/admin/clients/AddClientRoute'));
const AddClientCodeRoute = lazy(() => import('../features/admin/clients/AddClientCodeRoute'));
const AddClientContactRoute = lazy(() => import('../features/admin/clients/AddClientContactRoute'));
const AddClientAttributeRoute = lazy(
  () => import('../features/admin/clients/AddClientAttributeRoute')
);
const ClientDetailsRoute = lazy(() => import('../features/admin/clients/ClientDetailsRoute'));
const ClientCodeDetailsRoute = lazy(
  () => import('../features/admin/clients/ClientCodeDetailsRoute')
);
const AddClientDepartmentRoute = lazy(
  () => import('../features/admin/clients/AddClientDepartmentRoute')
);
const DepartmentDetailsRoute = lazy(
  () => import('../features/admin/clients/DepartmentDetailsRoute')
);
const ContactDetailsRoute = lazy(() => import('../features/admin/clients/ContactDetailsRoute'));
const UpdateAttributePage = lazy(
  () => import('../features/admin/clients/update-attribute/UpdateAttributePage')
);
const ClientMatrixRoute = lazy(
  () => import('../features/admin/clients/clientMatrix/ClientMatrixRoute')
);
const ClientScheduleOptionsRoute = lazy(
  () => import('../features/admin/clients/ClientScheduleOptionsRoute')
);
const ClientScheduleOptionsInfoRoute = lazy(
  () => import('../features/admin/clients/ClientScheduleOptionsInfoRoute')
);
const AddOrEditClientScheduleClientSetupRoute = lazy(
  () => import('../features/admin/clients/AddOrEditClientScheduleClientSetupRoute')
);
const EditClientScheduleWorkCodeSetupRoute = lazy(
  () => import('../features/admin/clients/EditClientScheduleWorkCodeSetupRoute')
);
const ClientAllowablesRoute = lazy(
  () => import('../features/admin/clients/clientAllowables/ClientAllowablesRoute')
);
const ClientAllowableDetailsRoute = lazy(
  () => import('../features/admin/clients/clientAllowableDetails/ClientAllowableDetailsRoute')
);
const ClientAllowableGroupLookupDetailsRoute = lazy(
  () =>
    import('../features/admin/clients/clientAllowableGroupLookupDetails/ClientAllowableGroupLookupDetailsRoute')
);
const LimitGroupNameDetailsRoute = lazy(
  () => import('../features/admin/clients/limitGroupNameDetails/LimitGroupNameDetailsRoute')
);
const TaskLibraryRoute = lazy(() => import('../features/admin/taskLibrary/TaskLibraryRoute'));
const UnlockMobileDevicesRoute = lazy(
  () => import('../features/vendorManagement/unlockMobileDevices/UnlockMobileDevicesRoute')
);
const ResendRegistrationEmailRoute = lazy(
  () => import('../features/vendorManagement/resendRegistrationEmail/ResendRegistrationEmailRoute')
);
const VendorMemosRoute = lazy(
  () => import('../features/vendorManagement/vendorMemos/VendorMemosRoute')
);
const AddVendorMemoRoute = lazy(
  () => import('../features/vendorManagement/vendorMemos/AddVendorMemoRoute')
);
const EditVendorMemoRoute = lazy(
  () => import('../features/vendorManagement/vendorMemos/EditVendorMemoRoute')
);
const UnlockUserRoute = lazy(
  () => import('../features/vendorManagement/unlockUser/UnlockUserRoute')
);
const SetExpeditedPaymentDateRoute = lazy(
  () => import('../features/vendorManagement/setExpeditedPaymentDate/SetExpeditedPaymentDateRoute')
);
const ZoneProfilesRoute = lazy(
  () => import('../features/vendorManagement/zoneProfiles/ZoneProfilesRoute')
);
const RemoveAllServiceOrdersRoute = lazy(
  () => import('../features/vendorManagement/removeAllServiceOrders/RemoveAllServiceOrdersRoute')
);
const SelfServiceOrdersRoute = lazy(
  () => import('../features/vendorManagement/selfServiceOrders/SelfServiceOrdersRoute')
);
const UploadIneligibleVendorListRoute = lazy(
  () =>
    import('../features/vendorManagement/uploadIneligibleVendorList/UploadIneligibleVendorListRoute')
);
const ServiceAreaSearchRoute = lazy(
  () => import('../features/vendorManagement/serviceAreaSearch/ServiceAreaSearchRoute')
);
const ServiceAreaUpdateRoute = lazy(
  () => import('../features/vendorManagement/serviceAreaUpdate/ServiceAreaUpdateRoute')
);
const ServiceAreaPricingRoute = lazy(
  () => import('../features/vendorManagement/serviceArea/pricing/ServiceAreaPricingRoute')
);
const ServiceAreaCreateRoute = lazy(
  () => import('../features/vendorManagement/serviceArea/create/ServiceAreaCreateRoute')
);
const UploadOppMapsListRoute = lazy(
  () => import('../features/vendorManagement/uploadOppMapsList/UploadOppMapsListRoute')
);
const UploadZonesListRoute = lazy(
  () => import('../features/vendorManagement/uploadZonesList/UploadZonesListRoute')
);
const UploadRankingListRoute = lazy(
  () => import('../features/vendorManagement/uploadRankingList/UploadRankingListRoute')
);
const RankFileManagementRoute = lazy(
  () => import('../features/vendorManagement/rankFileManagement/RankFileManagementRoute')
);
const VendorRegionsRoute = lazy(
  () => import('../features/vendorManagement/vendorRegions/VendorRegionsRoute')
);
const CompanyManagementRoute = lazy(
  () => import('../features/vendorManagement/companyManagement/CompanyManagementRoute')
);
const DocumentViewerRoute = lazy(
  () => import('../features/vendorManagement/documentViewer/DocumentViewerRoute')
);
const ServiceAreaContactRoute = lazy(
  () => import('../features/vendorManagement/serviceAreaContact/ServiceAreaContactRoute')
);
const CompanyMaintenanceRoute = lazy(
  () => import('../features/vendorManagement/companyMaintenance/CompanyMaintenanceRoute')
);
const CompanyDetailsRoute = lazy(
  () => import('../features/vendorManagement/companyDetails/CompanyDetailsRoute')
);
const ResourceMaintenanceRoute = lazy(
  () => import('../features/vendorManagement/resourceMaintenance/ResourceMaintenanceRoute')
);
const ViewResourceRoute = lazy(
  () => import('../features/vendorManagement/viewResource/ViewResourceRoute')
);
const EditBackgroundCheckRoute = lazy(
  () => import('../features/vendorManagement/backgroundCheck/EditBackgroundCheckRoute')
);
const SetCompanyAdminRoute = lazy(
  () => import('../features/vendorManagement/setCompanyAdmin/SetCompanyAdminRoute')
);
const CategoryMappingRoute = lazy(
  () => import('../features/invoice/catSubCat/categoryMapping/CategoryMappingRoute')
);
const AddCategoryMappingRoute = lazy(
  () => import('../features/invoice/catSubCat/addCategoryMapping/AddCategoryMappingRoute')
);
const ChangeSourceRoute = lazy(
  () => import('../features/invoice/catSubCat/changeSource/ChangeSourceRoute')
);
const AddChangeSourceRoute = lazy(
  () => import('../features/invoice/catSubCat/addChangeSource/AddChangeSourceRoute')
);
const EditChangeSourceRoute = lazy(
  () => import('../features/invoice/catSubCat/editChangeSource/EditChangeSourceRoute')
);
const UploadCategoryMappingRoute = lazy(
  () => import('../features/invoice/catSubCat/uploadCategoryMapping/UploadCategoryMappingRoute')
);
const VendorConnectRoute = lazy(() =>
  import('../features/vendorConnect/VendorConnectRoute').catch((err) => {
    console.error('[LazyLoad] VendorConnectRoute FAILED:', err);
    throw err;
  })
);
const VendorConnectOrderDetailsRoute = lazy(() =>
  import('../features/vendorConnect/VendorConnectOrderDetailsRoute').catch((err) => {
    console.error('[LazyLoad] VendorConnectOrderDetailsRoute FAILED:', err);
    throw err;
  })
);
const VendorAuthGuard = lazy(() =>
  import('../features/vendorConnect/auth/VendorAuthGuard').catch((err) => {
    console.error('[LazyLoad] VendorAuthGuard FAILED:', err);
    throw err;
  })
);
const Auth0CallbackHandler = lazy(() =>
  import('../features/vendorConnect/auth/Auth0CallbackHandler').catch((err) => {
    console.error('[LazyLoad] Auth0CallbackHandler FAILED:', err);
    throw err;
  })
);
const OaReportsRoute = lazy(() => import('../features/overallowables/OaReportsRoute'));
const ManageClientsRoute = lazy(() => import('../features/overallowables/ManageClientsRoute'));
const OverallowablesIndexRoute = lazy(
  () => import('../features/overallowables/overallowablesIndex/OverallowablesIndexRoute')
);
const InvoiceSummaryRoute = lazy(
  () => import('../features/invoice/invoiceSummary/InvoiceSummaryRoute')
);
const InvoiceDetailsRoute = lazy(
  () => import('../features/invoice/invoiceDetails/InvoiceDetailsRoute')
);
const OrderDetailsRoute = lazy(() => import('../features/invoice/orderDetails/OrderDetailsRoute'));
const InvoiceOnDemandRoute = lazy(
  () => import('../features/invoice/invoiceOnDemand/InvoiceOnDemandRoute')
);
const InvoiceOnDemand2Route = lazy(
  () => import('../features/invoice/invoiceOnDemand2/InvoiceOnDemand2Route')
);
const MessageDashboardRoute = lazy(
  () => import('../features/messageDashboard/MessageDashboardRoute')
);
const CaseManagementTestRoute = lazy(
  () => import('../features/caseManagement/CaseManagementTestRoute')
);
const ComplaintIntakeRoute = lazy(() => import('../features/caseManagement/pages/complaintIntake'));
const ComplaintResolutionCenterRoute = lazy(
  () => import('../features/caseManagement/pages/complaintResolutionCenter')
);
const ComplaintManagerOverviewRoute = lazy(
  () => import('../features/caseManagement/pages/complaintManagerOverview')
);
const GenericCaseRoute = lazy(() => import('../features/caseManagement/pages/genericCase'));
const PromptLibraryRoute = lazy(() => import('../features/admin/promptLibrary/PromptLibraryRoute'));
const StandardSetupRoute = lazy(() => import('../features/admin/scheduling/StandardSetupRoute'));
const StandardSetupOptionsRoute = lazy(
  () => import('../features/admin/scheduling/StandardSetupOptionsRoute')
);
const AttributesRoute = lazy(() => import('../features/admin/attributes/AttributesRoute'));
const AddAttributeRoute = lazy(() => import('../features/admin/attributes/AddAttributeRoute'));
const AttributeDetailsRoute = lazy(
  () => import('../features/admin/attributes/AttributeDetailsRoute')
);
const CategoriesListRoute = lazy(() => import('../features/admin/categories/CategoriesRoute'));
const AddCategoryRoute = lazy(() => import('../features/admin/categories/AddCategoryRoute'));
const CategoryDetailsRoute = lazy(
  () => import('../features/admin/categories/CategoryDetailsRoute')
);
const CategoriesByParentCategoryRoute = lazy(
  () => import('../features/admin/categories/CategoriesByParentCategoryRoute')
);
const CategoriesByParentCTDCategoryRoute = lazy(
  () => import('../features/admin/categories/CategoriesByParentCTDCategoryRoute')
);
const BidTaskXRefListRoute = lazy(
  () => import('../features/admin/categories/BidTaskXRefListRoute')
);
const AddBidTaskXrefRoute = lazy(() => import('../features/admin/categories/AddBidTaskXrefRoute'));
// ClientGrouping — StateGroup
const StateGroupListRoute = lazy(
  () => import('../features/admin/clientGrouping/StateGroupListRoute')
);
const AddStateGroupRoute = lazy(
  () => import('../features/admin/clientGrouping/AddStateGroupRoute')
);
const EditStateGroupRoute = lazy(
  () => import('../features/admin/clientGrouping/EditStateGroupRoute')
);
const ViewStateGroupRoute = lazy(
  () => import('../features/admin/clientGrouping/ViewStateGroupRoute')
);
// ClientGrouping — CityGroup
const CityGroupListRoute = lazy(
  () => import('../features/admin/clientGrouping/CityGroupListRoute')
);
const AddCityGroupRoute = lazy(() => import('../features/admin/clientGrouping/AddCityGroupRoute'));
const EditCityGroupRoute = lazy(
  () => import('../features/admin/clientGrouping/EditCityGroupRoute')
);
const ViewCityGroupRoute = lazy(
  () => import('../features/admin/clientGrouping/ViewCityGroupRoute')
);
// ClientGrouping — CountyGroup
const CountyGroupListRoute = lazy(
  () => import('../features/admin/clientGrouping/CountyGroupListRoute')
);
const AddCountyGroupRoute = lazy(
  () => import('../features/admin/clientGrouping/AddCountyGroupRoute')
);
const EditCountyGroupRoute = lazy(
  () => import('../features/admin/clientGrouping/EditCountyGroupRoute')
);
const ViewCountyGroupRoute = lazy(
  () => import('../features/admin/clientGrouping/ViewCountyGroupRoute')
);
// ClientGrouping — ZipCodeGroup
const ZipCodeGroupListRoute = lazy(
  () => import('../features/admin/clientGrouping/ZipCodeGroupListRoute')
);
const AddZipCodeGroupRoute = lazy(
  () => import('../features/admin/clientGrouping/AddZipCodeGroupRoute')
);
const EditZipCodeGroupRoute = lazy(
  () => import('../features/admin/clientGrouping/EditZipCodeGroupRoute')
);
const ViewZipCodeGroupRoute = lazy(
  () => import('../features/admin/clientGrouping/ViewZipCodeGroupRoute')
);
// ClientGrouping — WorkCodeGroup
const WorkCodeGroupListRoute = lazy(
  () => import('../features/admin/clientGrouping/WorkCodeGroupListRoute')
);
const AddWorkCodeGroupRoute = lazy(
  () => import('../features/admin/clientGrouping/AddWorkCodeGroupRoute')
);
const EditWorkCodeGroupRoute = lazy(
  () => import('../features/admin/clientGrouping/EditWorkCodeGroupRoute')
);
const ViewWorkCodeGroupRoute = lazy(
  () => import('../features/admin/clientGrouping/ViewWorkCodeGroupRoute')
);
// ClientGrouping — PunchCodeGroup
const PunchCodeGroupListRoute = lazy(
  () => import('../features/admin/clientGrouping/PunchCodeGroupListRoute')
);
const AddPunchCodeGroupRoute = lazy(
  () => import('../features/admin/clientGrouping/AddPunchCodeGroupRoute')
);
const EditPunchCodeGroupRoute = lazy(
  () => import('../features/admin/clientGrouping/EditPunchCodeGroupRoute')
);
const ViewPunchCodeGroupRoute = lazy(
  () => import('../features/admin/clientGrouping/ViewPunchCodeGroupRoute')
);
// ClientGrouping — AttributeGrouping
const AttributeGroupListRoute = lazy(
  () => import('../features/admin/clientGrouping/AttributeGroupListRoute')
);
const AddAttributeGroupRoute = lazy(
  () => import('../features/admin/clientGrouping/AddAttributeGroupRoute')
);
const EditAttributeGroupRoute = lazy(
  () => import('../features/admin/clientGrouping/EditAttributeGroupRoute')
);
const ViewAttributeGroupRoute = lazy(
  () => import('../features/admin/clientGrouping/ViewAttributeGroupRoute')
);
// Admin/Components
const ComponentsListRoute = lazy(() => import('../features/admin/components/ComponentsRoute'));
const AddComponentRoute = lazy(() => import('../features/admin/components/AddComponentRoute'));
const EditComponentRoute = lazy(() => import('../features/admin/components/EditComponentRoute'));
const ComponentSequenceListRoute = lazy(
  () => import('../features/admin/components/ComponentSequenceRoute')
);
const ComponentSequenceDetailsListRoute = lazy(
  () => import('../features/admin/components/ComponentSequenceDetailsRoute')
);
const AddComponentSequenceRoute = lazy(
  () => import('../features/admin/components/AddComponentSequenceRoute')
);
const EditComponentSequenceRoute = lazy(
  () => import('../features/admin/components/EditComponentSequenceRoute')
);
const ViewComponentSequenceRoute = lazy(
  () => import('../features/admin/components/ViewComponentSequenceRoute')
);
const ComponentTextListRoute = lazy(
  () => import('../features/admin/components/ComponentTextRoute')
);
const AddComponentTextRoute = lazy(
  () => import('../features/admin/components/AddComponentTextRoute')
);
const EditComponentTextRoute = lazy(
  () => import('../features/admin/components/EditComponentTextRoute')
);
const CompChangeSourceListRoute = lazy(
  () => import('../features/admin/components/ChangeSourceRoute')
);
const CompAddChangeSourceRoute = lazy(
  () => import('../features/admin/components/AddChangeSourceRoute')
);
const CompEditChangeSourceRoute = lazy(
  () => import('../features/admin/components/EditChangeSourceRoute')
);
const ChangeKeywordListRoute = lazy(
  () => import('../features/admin/components/ChangeKeywordRoute')
);
const AddKeywordRoute = lazy(() => import('../features/admin/components/AddKeywordRoute'));
const EditChangeKeywordRoute = lazy(
  () => import('../features/admin/components/EditChangeKeywordRoute')
);
const OrdinanceTextListRoute = lazy(
  () => import('../features/admin/components/OrdinanceTextRoute')
);
const AddOrdinanceTextRoute = lazy(
  () => import('../features/admin/components/AddOrdinanceTextRoute')
);
const EditOrdinanceTextRoute = lazy(
  () => import('../features/admin/components/EditOrdinanceTextRoute')
);
const AutoTextMaintenanceRoute = lazy(
  () => import('../features/admin/components/AutoTextMaintenanceRoute')
);
const EditAutoTextRoute = lazy(() => import('../features/admin/components/EditAutoTextRoute'));
const TestRenderingRoute = lazy(() => import('../features/admin/components/TestRenderingRoute'));

// Admin/MassUpload
const MassUploadRoute = lazy(() => import('../features/admin/massUpload/MassUploadRoute'));
const MassUploadDetailRoute = lazy(
  () => import('../features/admin/massUpload/MassUploadDetailRoute')
);

// Admin/Pricing
const PricingChangeSourceRoute = lazy(() => import('../features/admin/pricing/ChangeSourceRoute'));
const PricingAddChangeSourceRoute = lazy(
  () => import('../features/admin/pricing/AddChangeSourceRoute')
);
const PricingEditChangeSourceRoute = lazy(
  () => import('../features/admin/pricing/EditChangeSourceRoute')
);
const PricingClientLoanTypemapRoute = lazy(
  () => import('../features/admin/pricing/ClientLoanTypemapRoute')
);
const PricingAddClientLoanTypemapRoute = lazy(
  () => import('../features/admin/pricing/AddClientLoanTypemapRoute')
);
const PricingEditClientLoanTypemapRoute = lazy(
  () => import('../features/admin/pricing/EditClientLoanTypemapRoute')
);
const PricingClientTaskmapRoute = lazy(
  () => import('../features/admin/pricing/ClientTaskmapRoute')
);
const PricingAddClientTaskmapRoute = lazy(
  () => import('../features/admin/pricing/AddClientTaskmapRoute')
);
const PricingEditClientTaskmapRoute = lazy(
  () => import('../features/admin/pricing/EditClientTaskmapRoute')
);
const PricingClientRateLegacySearchRoute = lazy(
  () => import('../features/admin/pricing/ClientRateLegacySearchRoute')
);
const PricingClientTaskRateLookupRoute = lazy(
  () => import('../features/admin/pricing/ClientTaskRateLookupRoute')
);
const PricingAddClientTaskRateLookupRoute = lazy(
  () => import('../features/admin/pricing/AddClientTaskRateLookupRoute')
);
const PricingEditClientTaskRateLookupRoute = lazy(
  () => import('../features/admin/pricing/EditClientTaskRateLookupRoute')
);
const PricingCloneClientTaskRateLookupRoute = lazy(
  () => import('../features/admin/pricing/CloneClientTaskRateLookupRoute')
);
const PricingDefaultRateRoute = lazy(() => import('../features/admin/pricing/DefaultRateRoute'));
const PricingClientRangeSetupRoute = lazy(
  () => import('../features/admin/pricing/ClientRangeSetupRoute')
);
const PricingTestRenderingRoute = lazy(
  () => import('../features/admin/pricing/TestRenderingRoute')
);

// Admin/Allowables — AllowableServices
const AllowableServicesListRoute = lazy(
  () => import('../features/admin/allowables/allowableServices/ServicesListRoute')
);
const AllowableAddServiceRoute = lazy(
  () => import('../features/admin/allowables/allowableServices/AddServiceRoute')
);
const AllowableEditServiceRoute = lazy(
  () => import('../features/admin/allowables/allowableServices/EditServiceRoute')
);
const AllowableServiceDetailsRoute = lazy(
  () => import('../features/admin/allowables/allowableServices/ServiceDetailsRoute')
);
const UploadAllowableServicesRoute = lazy(
  () => import('../features/admin/allowables/allowableServices/UploadAllowableServicesRoute')
);
// Admin/Allowables — AllowableCategories
const AllowableCategoriesListRoute = lazy(
  () => import('../features/admin/allowables/allowableCategories/AllowableCategoriesListRoute')
);
const AllowableAddCategoryRoute = lazy(
  () => import('../features/admin/allowables/allowableCategories/AddCategoryRoute')
);
const AllowableEditCategoryRoute = lazy(
  () => import('../features/admin/allowables/allowableCategories/EditCategoryRoute')
);
const AllowableCategoryDetailsRoute = lazy(
  () => import('../features/admin/allowables/allowableCategories/AllowableCategoryDetailsRoute')
);
const UploadAllowableCategoriesRoute = lazy(
  () => import('../features/admin/allowables/allowableCategories/UploadAllowableCategoriesRoute')
);
// Admin/Allowables — AllowableTaskCategories
const AllowableTaskCategoriesListRoute = lazy(
  () =>
    import('../features/admin/allowables/allowableTaskCategories/AllowableTaskCategoriesListRoute')
);
const AddTaskCategoryRoute = lazy(
  () => import('../features/admin/allowables/allowableTaskCategories/AddTaskCategoryRoute')
);
const EditTaskCategoryRoute = lazy(
  () => import('../features/admin/allowables/allowableTaskCategories/EditTaskCategoryRoute')
);
const AllowableTaskCategoryDetailsRoute = lazy(
  () =>
    import('../features/admin/allowables/allowableTaskCategories/AllowableTaskCategoryDetailsRoute')
);
const UploadAllowableTaskCategoriesRoute = lazy(
  () =>
    import('../features/admin/allowables/allowableTaskCategories/UploadAllowableTaskCategoriesRoute')
);
// Admin/Allowables — AllowableTasks
const AllowableTaskListRoute = lazy(
  () => import('../features/admin/allowables/allowableTasks/AllowableTaskListRoute')
);
const AddAllowableTaskRoute = lazy(
  () => import('../features/admin/allowables/allowableTasks/AddAllowableTaskRoute')
);
const EditAllowableTaskRoute = lazy(
  () => import('../features/admin/allowables/allowableTasks/EditAllowableTaskRoute')
);
const AllowableTaskDetailsRoute = lazy(
  () => import('../features/admin/allowables/allowableTasks/AllowableTaskDetailsRoute')
);
const UploadAllowableTasksRoute = lazy(
  () => import('../features/admin/allowables/allowableTasks/UploadAllowableTasksRoute')
);
const UploadPunchCodesRoute = lazy(
  () => import('../features/admin/allowables/allowableTasks/UploadPunchCodesRoute')
);
const UploadTasktypesRoute = lazy(
  () => import('../features/admin/allowables/allowableTasks/UploadTasktypesRoute')
);
const UploadQuestiontypesRoute = lazy(
  () => import('../features/admin/allowables/allowableTasks/UploadQuestiontypesRoute')
);
// Admin/Allowables — AllowableGroup
const AllowableGroupListRoute = lazy(
  () => import('../features/admin/allowables/allowableGroup/AllowableGroupListRoute')
);
const AddAllowableGroupRoute = lazy(
  () => import('../features/admin/allowables/allowableGroup/AddAllowableGroupRoute')
);
const EditAllowableGroupRoute = lazy(
  () => import('../features/admin/allowables/allowableGroup/EditAllowableGroupRoute')
);
const AddAllowableGroupDetailRoute = lazy(
  () => import('../features/admin/allowables/allowableGroup/AddAllowableGroupDetailRoute')
);
const EditAllowableGroupDetailRoute = lazy(
  () => import('../features/admin/allowables/allowableGroup/EditAllowableGroupDetailRoute')
);
const AllowableGroupLookupDetailsRoute = lazy(
  () => import('../features/admin/allowables/allowableGroup/AllowableGroupLookupDetailsRoute')
);
const AllowableLimitGroupDetailsRoute = lazy(
  () => import('../features/admin/allowables/allowableGroup/AllowableLimitGroupDetailsRoute')
);
const AllowableAddAllowableServiceRoute = lazy(
  () => import('../features/admin/allowables/allowableGroup/AddAllowableServiceRoute')
);
const AllowableEditAllowableServiceRoute = lazy(
  () => import('../features/admin/allowables/allowableGroup/EditAllowableServiceRoute')
);
const UploadAllowableGroupLimitRoute = lazy(
  () => import('../features/admin/allowables/allowableGroup/UploadAllowableGroupLimitRoute')
);
const UploadClientsLoanTypesRoute = lazy(
  () => import('../features/admin/allowables/allowableGroup/UploadClientsLoanTypesRoute')
);
// Admin/Allowables — AllowableLimits
const AddLimitRoute = lazy(
  () => import('../features/admin/allowables/allowableLimits/AddLimitRoute')
);
const EditLimitRoute = lazy(
  () => import('../features/admin/allowables/allowableLimits/EditLimitRoute')
);
// Admin/Audit
const AnswersRoute = lazy(() => import('../features/admin/auditAdmin/AnswersRoute'));
const AnswerDetailsRoute = lazy(() => import('../features/admin/auditAdmin/AnswerDetailsRoute'));
const QuestionsRoute = lazy(() => import('../features/admin/auditAdmin/QuestionsRoute'));
const QuestionDetailsRoute = lazy(
  () => import('../features/admin/auditAdmin/QuestionDetailsRoute')
);
const RulesRoute = lazy(() => import('../features/admin/auditAdmin/RulesRoute'));
const RuleDetailsRoute = lazy(() => import('../features/admin/auditAdmin/RuleDetailsRoute'));
const TermsetRoute = lazy(() => import('../features/admin/auditAdmin/TermsetRoute'));
const QuestionAttributeRoute = lazy(
  () => import('../features/admin/auditAdmin/QuestionAttributeRoute')
);
const CreateResourceRoute = lazy(
  () => import('../features/vendorManagement/resourceMaintenance/CreateResourceRoute')
);

// VendorPayments routes
const AddPrePaymentRoute = lazy(() => import('../features/vendorPayments/AddPrePaymentRoute'));
const PrePaymentListRoute = lazy(() => import('../features/vendorPayments/PrePaymentListRoute'));
const AddVendorPenaltyRoute = lazy(
  () => import('../features/vendorPayments/AddVendorPenaltyRoute')
);
const VendorPenaltyListRoute = lazy(
  () => import('../features/vendorPayments/VendorPenaltyListRoute')
);

// Location routes
const VendorViewRoute = lazy(() => import('../features/location/VendorViewRoute'));
const GeomapRoute = lazy(() => import('../features/location/GeomapRoute'));

// Disputes routes
const DisputeSummaryRoute = lazy(() => import('../features/disputes/DisputeSummaryRoute'));
const AddDisputeRoute = lazy(() => import('../features/disputes/AddDisputeRoute'));
const EditDisputeRoute = lazy(() => import('../features/disputes/EditDisputeRoute'));

// AdvancedSearch routes
const AdvancedSearchRoute = lazy(() => import('../features/advancedSearch/AdvancedSearchRoute'));

// CoreScript routes
const AddText2Route = lazy(() => import('../features/coreScript/AddText2Route'));
const AuditRequest2Route = lazy(() => import('../features/coreScript/AuditRequest2Route'));
const VendorReassign2Route = lazy(() => import('../features/coreScript/VendorReassign2Route'));
const Schedule2Route = lazy(() => import('../features/coreScript/Schedule2Route'));
const AddVendorText2Route = lazy(() => import('../features/coreScript/AddVendorText2Route'));
const ICCStatus2Route = lazy(() => import('../features/coreScript/ICCStatus2Route'));
const ICCConfirmation2Route = lazy(() => import('../features/coreScript/ICCConfirmation2Route'));
const OrderModificationRequest2Route = lazy(
  () => import('../features/coreScript/OrderModificationRequest2Route')
);
const PostAuditRequest2Route = lazy(() => import('../features/coreScript/PostAuditRequest2Route'));
const CreateProperty2Route = lazy(() => import('../features/coreScript/CreateProperty2Route'));
const ConveyImpediments2Route = lazy(
  () => import('../features/coreScript/ConveyImpediments2Route')
);
const CodeViolationRequest2Route = lazy(
  () => import('../features/coreScript/CodeViolationRequest2Route')
);
const ReconveyRequest2Route = lazy(() => import('../features/coreScript/ReconveyRequest2Route'));
const SACC2Route = lazy(() => import('../features/coreScript/SACC2Route'));
const OrderEntry2Route = lazy(() => import('../features/coreScript/OrderEntry2Route'));

// Property routes
const PropertyRecordRoute = lazy(() => import('../features/property/PropertyRecordRoute'));
const MergeIdsRoute = lazy(() => import('../features/property/MergeIdsRoute'));
const ServiceReleaseRoute = lazy(() => import('../features/property/ServiceReleaseRoute'));
const CTDDetailsRoute = lazy(() => import('../features/property/CTDDetailsRoute'));
const CostToDateRoute = lazy(() => import('../features/property/CostToDateRoute'));
const LoanQualifierRoute = lazy(() => import('../features/property/LoanQualifierRoute'));
const TaskDetailRoute = lazy(() => import('../features/bids/TaskDetailRoute'));
const ApprovalDispositionRoute = lazy(() => import('../features/bids/ApprovalDispositionRoute'));
const TaskSummaryRoute = lazy(() => import('../features/bids/TaskSummaryRoute'));
const BidsSummaryRoute = lazy(() => import('../features/bids/BidsSummaryRoute'));
const BidsHistoryRoute = lazy(() => import('../features/bids/BidsHistoryRoute'));

// Bids routes
const ApprovalDetailRoute = lazy(() => import('../features/bids/ApprovalDetailRoute'));
const AuditScrubRoute = lazy(() => import('../features/bids/AuditScrubRoute'));
const AddBidRoute = lazy(() => import('../features/bids/AddBidRoute'));
const BidDetailRoute = lazy(() => import('../features/bids/BidDetailRoute'));
const BidDispositionRoute = lazy(() => import('../features/bids/BidDispositionRoute'));

// Damages routes
const DamagesHistoryRoute = lazy(() => import('../features/damages/DamagesHistoryRoute'));
const DamagesHistorySmallRoute = lazy(() => import('../features/damages/DamagesHistorySmallRoute'));
const DamagesSummaryRoute = lazy(() => import('../features/damages/DamagesSummaryRoute'));
const DamagesSummaryInspectionRoute = lazy(
  () => import('../features/damages/DamagesSummaryInspectionRoute')
);
const DamagesDetailsRoute = lazy(() => import('../features/damages/DamagesDetailsRoute'));

// MoreInformation route
const MoreInformationRoute = lazy(() => import('../features/moreInformation/MoreInformationRoute'));

// Task Activity route
const TaskActivityRoute = lazy(() => import('../features/taskActivity/TaskActivityRoute'));

// Audit Dashboard routes
const WorkOrderFinancialWorkFlowHistoryRoute = lazy(
  () => import('../features/auditDashboard/WorkOrderFinancialWorkFlowHistoryRoute')
);
const WorkOrderWorkFlowHistoryRoute = lazy(
  () => import('../features/auditDashboard/WorkOrderWorkFlowHistoryRoute')
);
const AdditionalOrdersRoute = lazy(
  () => import('../features/auditDashboard/AdditionalOrdersRoute')
);
const NoChargePayTaskRoute = lazy(() => import('../features/auditDashboard/NoChargePayTaskRoute'));
const OrderStatusTrackerRoute = lazy(
  () => import('../features/auditDashboard/OrderStatusTrackerRoute')
);
const AddTaskRoute = lazy(() => import('../features/auditDashboard/AddTaskRoute'));
const OverviewRoute = lazy(() => import('../features/auditDashboard/OverviewRoute'));
const WorkOrderDashboardRoute = lazy(
  () => import('../features/auditDashboard/WorkOrderDashboardRoute')
);
const ResultsRoute = lazy(() => import('../features/auditDashboard/ResultsRoute'));

const vendorManagementRootElement = <VendorManagementRoute />;

// VendorPayments elements
const addPrePaymentElement = (
  <Suspense fallback={<UiLoader />}>
    <AddPrePaymentRoute />
  </Suspense>
);
const prePaymentListElement = (
  <Suspense fallback={<UiLoader />}>
    <PrePaymentListRoute />
  </Suspense>
);
const addVendorPenaltyElement = (
  <Suspense fallback={<UiLoader />}>
    <AddVendorPenaltyRoute />
  </Suspense>
);
const vendorPenaltyListElement = (
  <Suspense fallback={<UiLoader />}>
    <VendorPenaltyListRoute />
  </Suspense>
);

const vendorViewElement = (
  <Suspense fallback={<UiLoader />}>
    <VendorViewRoute />
  </Suspense>
);

const geomapElement = (
  <Suspense fallback={<UiLoader />}>
    <GeomapRoute />
  </Suspense>
);

const disputeSummaryElement = (
  <Suspense fallback={<UiLoader />}>
    <DisputeSummaryRoute />
  </Suspense>
);
const addDisputeElement = (
  <Suspense fallback={<UiLoader />}>
    <AddDisputeRoute />
  </Suspense>
);
const editDisputeElement = (
  <Suspense fallback={<UiLoader />}>
    <EditDisputeRoute />
  </Suspense>
);

const advancedSearchElement = (
  <Suspense fallback={<UiLoader />}>
    <AdvancedSearchRoute />
  </Suspense>
);

const addText2Element = (
  <Suspense fallback={<UiLoader />}>
    <AddText2Route />
  </Suspense>
);

const sacc2Element = (
  <Suspense fallback={<UiLoader />}>
    <SACC2Route />
  </Suspense>
);

const unlockMobileElement = <UnlockMobileDevicesRoute />;

const unlockUserElement = <UnlockUserRoute />;

const setExpeditedPaymentDateElement = <SetExpeditedPaymentDateRoute />;

const resendRegistrationEmailElement = <ResendRegistrationEmailRoute />;

const vendorMemosElement = <VendorMemosRoute />;
const addVendorMemoElement = <AddVendorMemoRoute />;
const editVendorMemoElement = <EditVendorMemoRoute />;

const zoneProfilesElement = <ZoneProfilesRoute />;

const removeAllServiceOrdersElement = <RemoveAllServiceOrdersRoute />;

const selfServiceOrdersElement = <SelfServiceOrdersRoute />;

const uploadIneligibleVendorListElement = <UploadIneligibleVendorListRoute />;

const serviceAreaSearchElement = <ServiceAreaSearchRoute />;

const serviceAreaUpdateElement = <ServiceAreaUpdateRoute />;

const serviceAreaPricingElement = <ServiceAreaPricingRoute />;

const serviceAreaCreateElement = <ServiceAreaCreateRoute />;

const uploadOppMapsListElement = <UploadOppMapsListRoute />;

const uploadZonesListElement = <UploadZonesListRoute />;

const uploadRankingListElement = <UploadRankingListRoute />;

const rankFileManagementElement = <RankFileManagementRoute />;

const vendorRegionsElement = <VendorRegionsRoute />;
const serviceAreaContactElement = <ServiceAreaContactRoute />;

const companyManagementElement = <CompanyManagementRoute />;

const documentViewerElement = <DocumentViewerRoute />;

const companyMaintenanceElement = <CompanyMaintenanceRoute />;

const companyDetailsElement = <CompanyDetailsRoute />;

const resourceMaintenanceElement = <ResourceMaintenanceRoute />;
const viewResourceElement = <ViewResourceRoute />;
const editBackgroundCheckElement = <EditBackgroundCheckRoute />;
const setCompanyAdminElement = <SetCompanyAdminRoute />;

const withCaseManagementStyles = (element: JSX.Element) => (
  <CaseManagementStyleProvider>{element}</CaseManagementStyleProvider>
);

const routeConfig = [
  {
    path: '/login',
    element: (
      <Suspense fallback={<UiLoader />}>
        <LoginPage />
      </Suspense>
    ),
  },
  {
    path: '/',
    element: (
      <AuthGate>
        <AppLayout>
          <Outlet />
        </AppLayout>
      </AuthGate>
    ),
    errorElement: <RouteError />,
    children: [
      {
        index: true,
        element: <Navigate to='/home' replace />,
      },
      {
        path: 'home',
        element: withCaseManagementStyles(
          <Suspense fallback={<UiLoader />}>
            <ComplaintResolutionCenterRoute />
          </Suspense>
        ),
      },
      {
        path: 'Home/dashboard',
        element: <Navigate to='/home' replace />,
      },
      {
        path: 'Admin/Clients/client',
        element: <ClientListRoute />,
      },
      {
        path: 'Admin/Clients/AddClient',
        element: <AddClientRoute />,
      },
      {
        path: 'Admin/Clients/AddClientCode',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AddClientCodeRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Clients/AddClientContact',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AddClientContactRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Clients/clientDetails/:clientId',
        element: <ClientDetailsRoute />,
      },
      {
        path: 'Admin/Clients/clientCodeDetails/:clientCodeId',
        element: <ClientCodeDetailsRoute />,
      },
      {
        path: 'Admin/Clients/AddClientDepartment',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AddClientDepartmentRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Clients/departmentDetails/:departmentId',
        element: <DepartmentDetailsRoute />,
      },
      {
        path: 'Admin/Clients/contactDetails/:contactId',
        element: <ContactDetailsRoute />,
      },
      {
        path: 'Admin/Clients/AddAttribute',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AddClientAttributeRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Clients/UpdateAttribute',
        element: <UpdateAttributePage />,
      },
      {
        path: 'Admin/Clients/UpdateAttribute/:attributeId',
        element: <UpdateAttributePage />,
      },
      {
        path: 'Admin/Clients/ClientMatrix',
        element: (
          <Suspense fallback={<UiLoader />}>
            <ClientMatrixRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Clients/ClientScheduleOptions/ClientScheduleOptionsList',
        element: (
          <Suspense fallback={<UiLoader />}>
            <ClientScheduleOptionsRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Clients/ClientScheduleOptions/ClientScheduleOptionsList/:idAndCode',
        element: (
          <Suspense fallback={<UiLoader />}>
            <ClientScheduleOptionsRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Clients/ClientScheduleOptions/ClientScheduleOptionsInfo',
        element: (
          <Suspense fallback={<UiLoader />}>
            <ClientScheduleOptionsInfoRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Clients/ClientScheduleOptions/AddOrEditClientScheduleClientSetup',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AddOrEditClientScheduleClientSetupRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Clients/ClientScheduleOptions/EditClientScheduleWorkCodeSetup',
        element: (
          <Suspense fallback={<UiLoader />}>
            <EditClientScheduleWorkCodeSetupRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Clients/ClientAllowables',
        element: (
          <Suspense fallback={<UiLoader />}>
            <ClientAllowablesRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Clients/ClientAllowableDetails',
        element: (
          <Suspense fallback={<UiLoader />}>
            <ClientAllowableDetailsRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Clients/ClientAllowableGroupLookupDetails',
        element: (
          <Suspense fallback={<UiLoader />}>
            <ClientAllowableGroupLookupDetailsRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Clients/LimitGroupNameDetails',
        element: (
          <Suspense fallback={<UiLoader />}>
            <LimitGroupNameDetailsRoute />
          </Suspense>
        ),
      },
      {
        path: 'Invoice/CatSubCat/CategoryMapping',
        element: (
          <Suspense fallback={<UiLoader />}>
            <CategoryMappingRoute />
          </Suspense>
        ),
      },
      {
        path: 'Invoice/CatSubCat/AddCategoryMapping',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AddCategoryMappingRoute />
          </Suspense>
        ),
      },
      {
        path: 'Invoice/CatSubCat/ChangeSource',
        element: (
          <Suspense fallback={<UiLoader />}>
            <ChangeSourceRoute />
          </Suspense>
        ),
      },
      {
        path: 'Invoice/CatSubCat/AddChangeSource',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AddChangeSourceRoute />
          </Suspense>
        ),
      },
      {
        path: 'Invoice/CatSubCat/EditChangeSource',
        element: (
          <Suspense fallback={<UiLoader />}>
            <EditChangeSourceRoute />
          </Suspense>
        ),
      },
      {
        path: 'Invoice/CatSubCat/UploadCategoryMapping',
        element: (
          <Suspense fallback={<UiLoader />}>
            <UploadCategoryMappingRoute />
          </Suspense>
        ),
      },
      {
        path: 'Overallowables/oa_reports',
        element: (
          <Suspense fallback={<UiLoader />}>
            <OaReportsRoute />
          </Suspense>
        ),
      },
      {
        path: 'Overallowables/manage_client',
        element: (
          <Suspense fallback={<UiLoader />}>
            <ManageClientsRoute />
          </Suspense>
        ),
      },
      {
        path: 'Overallowables/index',
        element: (
          <Suspense fallback={<UiLoader />}>
            <OverallowablesIndexRoute />
          </Suspense>
        ),
      },
      {
        path: 'Invoice/InvoiceSummary',
        element: (
          <ProtectedRoute requiredPermissions={['invoiceReader']}>
            <Suspense fallback={<UiLoader />}>
              <InvoiceSummaryRoute />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'Invoice/InvoiceDetails',
        element: (
          <ProtectedRoute requiredPermissions={['invoiceReader']}>
            <Suspense fallback={<UiLoader />}>
              <InvoiceDetailsRoute />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'Invoice/OrderDetails',
        element: (
          <ProtectedRoute requiredPermissions={['invoiceReader']}>
            <Suspense fallback={<UiLoader />}>
              <OrderDetailsRoute />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'Invoice/InvoiceOnDemand',
        element: (
          <ProtectedRoute requiredPermissions={['invoiceOnDemand']}>
            <Suspense fallback={<UiLoader />}>
              <InvoiceOnDemandRoute />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'Invoice/InvoiceOnDemand2',
        element: (
          <ProtectedRoute requiredPermissions={['invoiceOnDemand2']}>
            <Suspense fallback={<UiLoader />}>
              <InvoiceOnDemand2Route />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'MessageDashboard',
        element: (
          <Suspense fallback={<UiLoader />}>
            <MessageDashboardRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/PromptLibrary',
        element: (
          <Suspense fallback={<UiLoader />}>
            <PromptLibraryRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Scheduling/StandardSetup/StandardSetup',
        element: (
          <Suspense fallback={<UiLoader />}>
            <StandardSetupRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Scheduling/StandardSetupOptions/StandardSetupOptions',
        element: (
          <Suspense fallback={<UiLoader />}>
            <StandardSetupOptionsRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Attributes/Attributes',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AttributesRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Attributes/AddAttribute',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AddAttributeRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Attributes/AttributeDetails',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AttributeDetailsRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Categories/Categories',
        element: (
          <Suspense fallback={<UiLoader />}>
            <CategoriesListRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Categories/AddCategory',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AddCategoryRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Categories/CategoryDetails',
        element: (
          <Suspense fallback={<UiLoader />}>
            <CategoryDetailsRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Categories/CategoriesByParentCategory',
        element: (
          <Suspense fallback={<UiLoader />}>
            <CategoriesByParentCategoryRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Categories/CategoriesByParentCTDCategory',
        element: (
          <Suspense fallback={<UiLoader />}>
            <CategoriesByParentCTDCategoryRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Categories/BidTaskXRefList',
        element: (
          <Suspense fallback={<UiLoader />}>
            <BidTaskXRefListRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Categories/AddBidTaskXref',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AddBidTaskXrefRoute />
          </Suspense>
        ),
      },
      // ClientGrouping — StateGroup
      {
        path: 'Admin/ClientGrouping/StateGroup/StateGroupList',
        element: (
          <Suspense fallback={<UiLoader />}>
            <StateGroupListRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/ClientGrouping/StateGroup/AddStateGroup',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AddStateGroupRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/ClientGrouping/StateGroup/EditStateGroup',
        element: (
          <Suspense fallback={<UiLoader />}>
            <EditStateGroupRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/ClientGrouping/StateGroup/ViewStateGroup',
        element: (
          <Suspense fallback={<UiLoader />}>
            <ViewStateGroupRoute />
          </Suspense>
        ),
      },
      // ClientGrouping — CityGroup
      {
        path: 'Admin/ClientGrouping/CityGroup/CityGroupList',
        element: (
          <Suspense fallback={<UiLoader />}>
            <CityGroupListRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/ClientGrouping/CityGroup/AddCityGroup',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AddCityGroupRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/ClientGrouping/CityGroup/EditCityGroup',
        element: (
          <Suspense fallback={<UiLoader />}>
            <EditCityGroupRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/ClientGrouping/CityGroup/ViewCityGroup',
        element: (
          <Suspense fallback={<UiLoader />}>
            <ViewCityGroupRoute />
          </Suspense>
        ),
      },
      // ClientGrouping — CountyGroup
      {
        path: 'Admin/ClientGrouping/CountyGroup/CountyGroupList',
        element: (
          <Suspense fallback={<UiLoader />}>
            <CountyGroupListRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/ClientGrouping/CountyGroup/AddCountyGroup',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AddCountyGroupRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/ClientGrouping/CountyGroup/EditCountyGroup',
        element: (
          <Suspense fallback={<UiLoader />}>
            <EditCountyGroupRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/ClientGrouping/CountyGroup/ViewCountyGroup',
        element: (
          <Suspense fallback={<UiLoader />}>
            <ViewCountyGroupRoute />
          </Suspense>
        ),
      },
      // ClientGrouping — ZipCodeGroup
      {
        path: 'Admin/ClientGrouping/ZipCodeGroup/ZipCodeGroupList',
        element: (
          <Suspense fallback={<UiLoader />}>
            <ZipCodeGroupListRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/ClientGrouping/ZipCodeGroup/AddZipCodeGroup',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AddZipCodeGroupRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/ClientGrouping/ZipCodeGroup/EditZipCodeGroup',
        element: (
          <Suspense fallback={<UiLoader />}>
            <EditZipCodeGroupRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/ClientGrouping/ZipCodeGroup/ViewZipCodeGroup',
        element: (
          <Suspense fallback={<UiLoader />}>
            <ViewZipCodeGroupRoute />
          </Suspense>
        ),
      },
      // ClientGrouping — WorkCodeGroup
      {
        path: 'Admin/ClientGrouping/WorkCodeGroup/WorkCodeGroupList',
        element: (
          <Suspense fallback={<UiLoader />}>
            <WorkCodeGroupListRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/ClientGrouping/WorkCodeGroup/AddWorkCodeGroup',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AddWorkCodeGroupRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/ClientGrouping/WorkCodeGroup/EditWorkCodeGroup',
        element: (
          <Suspense fallback={<UiLoader />}>
            <EditWorkCodeGroupRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/ClientGrouping/WorkCodeGroup/ViewWorkCodeGroup',
        element: (
          <Suspense fallback={<UiLoader />}>
            <ViewWorkCodeGroupRoute />
          </Suspense>
        ),
      },
      // ClientGrouping — PunchCodeGroup
      {
        path: 'Admin/ClientGrouping/PunchCodeGroup/PunchCodeGroupList',
        element: (
          <Suspense fallback={<UiLoader />}>
            <PunchCodeGroupListRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/ClientGrouping/PunchCodeGroup/AddPunchCodeGroup',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AddPunchCodeGroupRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/ClientGrouping/PunchCodeGroup/EditPunchCodeGroup',
        element: (
          <Suspense fallback={<UiLoader />}>
            <EditPunchCodeGroupRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/ClientGrouping/PunchCodeGroup/ViewPunchCodeGroup',
        element: (
          <Suspense fallback={<UiLoader />}>
            <ViewPunchCodeGroupRoute />
          </Suspense>
        ),
      },
      // ClientGrouping — AttributeGrouping
      {
        path: 'Admin/ClientGrouping/AttributeGrouping/AttributeGroupList',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AttributeGroupListRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/ClientGrouping/AttributeGrouping/AddAttributeGroup',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AddAttributeGroupRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/ClientGrouping/AttributeGrouping/EditAttributeGroup',
        element: (
          <Suspense fallback={<UiLoader />}>
            <EditAttributeGroupRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/ClientGrouping/AttributeGrouping/ViewAttributeGroup',
        element: (
          <Suspense fallback={<UiLoader />}>
            <ViewAttributeGroupRoute />
          </Suspense>
        ),
      },
      // Admin/Components
      {
        path: 'Admin/Components/Components',
        element: (
          <Suspense fallback={<UiLoader />}>
            <ComponentsListRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Components/AddComponent',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AddComponentRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Components/EditComponent',
        element: (
          <Suspense fallback={<UiLoader />}>
            <EditComponentRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Components/ComponentSequence',
        element: (
          <Suspense fallback={<UiLoader />}>
            <ComponentSequenceListRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Components/ComponentSequenceDetails',
        element: (
          <Suspense fallback={<UiLoader />}>
            <ComponentSequenceDetailsListRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Components/AddComponentSequence',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AddComponentSequenceRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Components/EditComponentSequence',
        element: (
          <Suspense fallback={<UiLoader />}>
            <EditComponentSequenceRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Components/ViewComponentSequence',
        element: (
          <Suspense fallback={<UiLoader />}>
            <ViewComponentSequenceRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Components/ComponentText',
        element: (
          <Suspense fallback={<UiLoader />}>
            <ComponentTextListRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Components/AddComponentText',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AddComponentTextRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Components/EditComponentText',
        element: (
          <Suspense fallback={<UiLoader />}>
            <EditComponentTextRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Components/ChangeSource',
        element: (
          <Suspense fallback={<UiLoader />}>
            <CompChangeSourceListRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Components/AddChangeSource',
        element: (
          <Suspense fallback={<UiLoader />}>
            <CompAddChangeSourceRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Components/EditChangeSource',
        element: (
          <Suspense fallback={<UiLoader />}>
            <CompEditChangeSourceRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Components/ChangeKeyword',
        element: (
          <Suspense fallback={<UiLoader />}>
            <ChangeKeywordListRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Components/AddKeyword',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AddKeywordRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Components/EditChangeKeyword',
        element: (
          <Suspense fallback={<UiLoader />}>
            <EditChangeKeywordRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Components/OrdinanceText',
        element: (
          <Suspense fallback={<UiLoader />}>
            <OrdinanceTextListRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Components/AddOrdinanceText',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AddOrdinanceTextRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Components/EditOrdinanceText',
        element: (
          <Suspense fallback={<UiLoader />}>
            <EditOrdinanceTextRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Components/AutoTextMaintenance',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AutoTextMaintenanceRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Components/EditAutoText',
        element: (
          <Suspense fallback={<UiLoader />}>
            <EditAutoTextRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Components/TestRendering',
        element: (
          <Suspense fallback={<UiLoader />}>
            <TestRenderingRoute />
          </Suspense>
        ),
      },
      // Admin/MassUpload
      {
        path: 'Admin/MassUpload',
        element: (
          <Suspense fallback={<UiLoader />}>
            <MassUploadRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/MassUploadDetail/:id',
        element: (
          <Suspense fallback={<UiLoader />}>
            <MassUploadDetailRoute />
          </Suspense>
        ),
      },
      // Admin/Allowables — AllowableServices
      {
        path: 'Admin/Allowables/AllowableServices/ServicesList',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AllowableServicesListRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Allowables/AllowableServices/AddService',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AllowableAddServiceRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Allowables/AllowableServices/EditService',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AllowableEditServiceRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Allowables/AllowableServices/ServiceDetails',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AllowableServiceDetailsRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Allowables/AllowableServices/UploadAllowableServices',
        element: (
          <Suspense fallback={<UiLoader />}>
            <UploadAllowableServicesRoute />
          </Suspense>
        ),
      },
      // Admin/Allowables — AllowableCategories
      {
        path: 'Admin/Allowables/AllowableCategories/AllowableCategoriesList',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AllowableCategoriesListRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Allowables/AllowableCategories/AddCategory',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AllowableAddCategoryRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Allowables/AllowableCategories/EditCategory',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AllowableEditCategoryRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Allowables/AllowableCategories/AllowableCategoryDetails',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AllowableCategoryDetailsRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Allowables/AllowableCategories/UploadAllowableCategories',
        element: (
          <Suspense fallback={<UiLoader />}>
            <UploadAllowableCategoriesRoute />
          </Suspense>
        ),
      },
      // Admin/Allowables — AllowableTaskCategories
      {
        path: 'Admin/Allowables/AllowableTaskCategories/AllowableTaskCategoriesList',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AllowableTaskCategoriesListRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Allowables/AllowableTaskCategories/AddTaskCategory',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AddTaskCategoryRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Allowables/AllowableTaskCategories/EditTaskCategory',
        element: (
          <Suspense fallback={<UiLoader />}>
            <EditTaskCategoryRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Allowables/AllowableTaskCategories/AllowableTaskCategoryDetails',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AllowableTaskCategoryDetailsRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Allowables/AllowableTaskCategories/UploadAllowableTaskCategories',
        element: (
          <Suspense fallback={<UiLoader />}>
            <UploadAllowableTaskCategoriesRoute />
          </Suspense>
        ),
      },
      // Admin/Allowables — AllowableTasks
      {
        path: 'Admin/Allowables/AllowableTasks/AllowableTaskList',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AllowableTaskListRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Allowables/AllowableTasks/AddAllowableTask',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AddAllowableTaskRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Allowables/AllowableTasks/EditAllowableTask',
        element: (
          <Suspense fallback={<UiLoader />}>
            <EditAllowableTaskRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Allowables/AllowableTasks/AllowableTaskDetails',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AllowableTaskDetailsRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Allowables/AllowableTasks/UploadAllowableTasks',
        element: (
          <Suspense fallback={<UiLoader />}>
            <UploadAllowableTasksRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Allowables/AllowableTasks/UploadPunchCodes',
        element: (
          <Suspense fallback={<UiLoader />}>
            <UploadPunchCodesRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Allowables/AllowableTasks/UploadTasktypes',
        element: (
          <Suspense fallback={<UiLoader />}>
            <UploadTasktypesRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Allowables/AllowableTasks/UploadQuestiontypes',
        element: (
          <Suspense fallback={<UiLoader />}>
            <UploadQuestiontypesRoute />
          </Suspense>
        ),
      },
      // Admin/Allowables — AllowableGroup
      {
        path: 'Admin/Allowables/AllowableGroup/AllowableGroupList',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AllowableGroupListRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Allowables/AllowableGroup/AddAllowableGroup',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AddAllowableGroupRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Allowables/AllowableGroup/EditAllowableGroup',
        element: (
          <Suspense fallback={<UiLoader />}>
            <EditAllowableGroupRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Allowables/AllowableGroup/AddAllowableGroupDetail',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AddAllowableGroupDetailRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Allowables/AllowableGroup/EditAllowableGroupDetail',
        element: (
          <Suspense fallback={<UiLoader />}>
            <EditAllowableGroupDetailRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Allowables/AllowableGroup/AllowableGroupLookupDetails',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AllowableGroupLookupDetailsRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Allowables/AllowableGroup/AllowableLimitGroupDetails',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AllowableLimitGroupDetailsRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Allowables/AllowableGroup/AddAllowableService',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AllowableAddAllowableServiceRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Allowables/AllowableGroup/EditAllowableService',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AllowableEditAllowableServiceRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Allowables/AllowableGroup/UploadAllowableGroupLimit',
        element: (
          <Suspense fallback={<UiLoader />}>
            <UploadAllowableGroupLimitRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Allowables/AllowableGroup/UploadClientsLoanTypes',
        element: (
          <Suspense fallback={<UiLoader />}>
            <UploadClientsLoanTypesRoute />
          </Suspense>
        ),
      },
      // Admin/Allowables — AllowableLimits
      {
        path: 'Admin/Allowables/AllowableLimits/AddLimit',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AddLimitRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Allowables/AllowableLimits/EditLimit',
        element: (
          <Suspense fallback={<UiLoader />}>
            <EditLimitRoute />
          </Suspense>
        ),
      },
      // Admin/Audit
      {
        path: 'Admin/Audit/Answer/Answers',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AnswersRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Audit/Answer/AnswerDetails',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AnswerDetailsRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Audit/Question/Questions',
        element: (
          <Suspense fallback={<UiLoader />}>
            <QuestionsRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Audit/Question/QuestionDetails',
        element: (
          <Suspense fallback={<UiLoader />}>
            <QuestionDetailsRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Audit/Rule/Rules',
        element: (
          <Suspense fallback={<UiLoader />}>
            <RulesRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Audit/Rule/RuleDetails',
        element: (
          <Suspense fallback={<UiLoader />}>
            <RuleDetailsRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Audit/Termset',
        element: (
          <Suspense fallback={<UiLoader />}>
            <TermsetRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Audit/Termset/Termset',
        element: <Navigate to='/Admin/Audit/Termset' replace />,
      },
      {
        path: 'Admin/Audit/QuestionAttribute',
        element: (
          <Suspense fallback={<UiLoader />}>
            <QuestionAttributeRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Audit/QuestionAttribute/QuestionAttributes',
        element: <Navigate to='/Admin/Audit/QuestionAttribute' replace />,
      },
      // Admin/Pricing
      {
        path: 'Admin/Pricing/ChangeSource',
        element: (
          <Suspense fallback={<UiLoader />}>
            <PricingChangeSourceRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Pricing/AddChangeSource',
        element: (
          <Suspense fallback={<UiLoader />}>
            <PricingAddChangeSourceRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Pricing/EditChangeSource',
        element: (
          <Suspense fallback={<UiLoader />}>
            <PricingEditChangeSourceRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Pricing/ClientPricing/ClientLoanTypemap/ClientLoanTypemapList',
        element: (
          <Suspense fallback={<UiLoader />}>
            <PricingClientLoanTypemapRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Pricing/ClientPricing/ClientLoanTypemap/AddClientLoanTypemap',
        element: (
          <Suspense fallback={<UiLoader />}>
            <PricingAddClientLoanTypemapRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Pricing/ClientPricing/ClientLoanTypemap/EditClientLoanTypemap',
        element: (
          <Suspense fallback={<UiLoader />}>
            <PricingEditClientLoanTypemapRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Pricing/ClientPricing/ClientTaskmap/ClientTaskmapList',
        element: (
          <Suspense fallback={<UiLoader />}>
            <PricingClientTaskmapRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Pricing/ClientPricing/ClientTaskmap/AddClientTaskmap',
        element: (
          <Suspense fallback={<UiLoader />}>
            <PricingAddClientTaskmapRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Pricing/ClientPricing/ClientTaskmap/EditClientTaskmap',
        element: (
          <Suspense fallback={<UiLoader />}>
            <PricingEditClientTaskmapRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Pricing/ClientPricing/ClientTaskRateLookup/ClientRateLegacySearch',
        element: (
          <Suspense fallback={<UiLoader />}>
            <PricingClientRateLegacySearchRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Pricing/ClientPricing/ClientTaskRateLookup/ClientTaskRateLookupList',
        element: (
          <Suspense fallback={<UiLoader />}>
            <PricingClientTaskRateLookupRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Pricing/ClientPricing/ClientTaskRateLookup/AddClientTaskRateLookup',
        element: (
          <Suspense fallback={<UiLoader />}>
            <PricingAddClientTaskRateLookupRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Pricing/ClientPricing/ClientTaskRateLookup/EditClientTaskRateLookup',
        element: (
          <Suspense fallback={<UiLoader />}>
            <PricingEditClientTaskRateLookupRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Pricing/ClientPricing/ClientTaskRateLookup/CloneClientTaskRateLookup',
        element: (
          <Suspense fallback={<UiLoader />}>
            <PricingCloneClientTaskRateLookupRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Pricing/ClientPricing/DefaultRate/DefaultRate',
        element: (
          <Suspense fallback={<UiLoader />}>
            <PricingDefaultRateRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Pricing/TestRendering/TestRendering',
        element: (
          <Suspense fallback={<UiLoader />}>
            <PricingTestRenderingRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/Pricing/ClientPricing/ClientRangeSetup',
        element: (
          <Suspense fallback={<UiLoader />}>
            <PricingClientRangeSetupRoute />
          </Suspense>
        ),
      },
      {
        path: 'Admin/TaskLibrary',
        element: <TaskLibraryRoute />,
      },
      {
        path: 'vendor-management',
        element: vendorManagementRootElement,
      },
      {
        path: 'VendorManagement/VendorManagement',
        element: vendorManagementRootElement,
      },
      {
        path: 'vendor-management/unlock-mobile',
        element: unlockMobileElement,
      },
      {
        path: 'VendorManagement/UnlockMobileDevices',
        element: unlockMobileElement,
      },
      {
        path: 'vendor-management/unlock-user',
        element: unlockUserElement,
      },
      {
        path: 'VendorManagement/UnlockUser',
        element: unlockUserElement,
      },
      {
        path: 'vendor-management/set-expedited-payment-date',
        element: setExpeditedPaymentDateElement,
      },
      {
        path: 'VendorManagement/SetExpeditedPaymentDate',
        element: setExpeditedPaymentDateElement,
      },
      {
        path: 'vendor-management/resend-registration-email',
        element: resendRegistrationEmailElement,
      },
      {
        path: 'VendorManagement/ResendRegistrationEmail',
        element: resendRegistrationEmailElement,
      },
      {
        path: 'vendor-management/vendor-memos',
        element: vendorMemosElement,
      },
      {
        path: 'VendorManagement/VendorMemos/VendorMemosList',
        element: vendorMemosElement,
      },
      {
        path: 'vendor-management/vendor-memos/add',
        element: addVendorMemoElement,
      },
      {
        path: 'VendorManagement/VendorMemos/AddVendorMemo',
        element: addVendorMemoElement,
      },
      {
        path: 'vendor-management/vendor-memos/edit',
        element: editVendorMemoElement,
      },
      {
        path: 'VendorManagement/VendorMemos/EditVendorMemo',
        element: editVendorMemoElement,
      },
      {
        path: 'vendor-management/zone-profiles',
        element: zoneProfilesElement,
      },
      {
        path: 'VendorManagement/ZoneProfiles',
        element: zoneProfilesElement,
      },
      {
        path: 'vendor-management/remove-all-service-orders',
        element: removeAllServiceOrdersElement,
      },
      {
        path: 'VendorManagement/RemoveAllServiceOrders',
        element: removeAllServiceOrdersElement,
      },
      {
        path: 'vendor-management/self-service-orders',
        element: selfServiceOrdersElement,
      },
      {
        path: 'VendorManagement/SelfServiceOrders',
        element: selfServiceOrdersElement,
      },
      {
        path: 'vendor-management/upload-ineligible-vendor-list',
        element: uploadIneligibleVendorListElement,
      },
      {
        path: 'VendorManagement/UploadIneligibleVendorList',
        element: uploadIneligibleVendorListElement,
      },
      {
        path: 'vendor-management/service-area-search',
        element: serviceAreaSearchElement,
      },
      {
        path: 'VendorManagement/ServiceAreaSearch',
        element: serviceAreaSearchElement,
      },
      {
        path: 'vendor-management/service-area-update',
        element: serviceAreaUpdateElement,
      },
      {
        path: 'VendorManagement/ServiceArea/Update',
        element: serviceAreaUpdateElement,
      },
      {
        path: 'vendor-management/service-area/pricing',
        element: serviceAreaPricingElement,
      },
      {
        path: 'VendorManagement/ServiceArea/Pricing',
        element: serviceAreaPricingElement,
      },
      {
        path: 'vendor-management/service-area/create',
        element: serviceAreaCreateElement,
      },
      {
        path: 'VendorManagement/ServiceArea/Create',
        element: serviceAreaCreateElement,
      },
      {
        path: 'vendor-management/service-area/contact',
        element: serviceAreaContactElement,
      },
      {
        path: 'VendorManagement/ServiceArea/Contact',
        element: serviceAreaContactElement,
      },
      {
        path: '/vendor-management/service-area/contact',
        element: serviceAreaContactElement,
      },
      {
        path: '/VendorManagement/ServiceArea/Contact',
        element: serviceAreaContactElement,
      },
      {
        path: 'vendor-management/upload-opp-maps-list',
        element: uploadOppMapsListElement,
      },
      {
        path: 'VendorManagement/UploadOppMapsList',
        element: uploadOppMapsListElement,
      },
      {
        path: 'vendor-management/upload-zones-list',
        element: uploadZonesListElement,
      },
      {
        path: 'VendorManagement/UploadZonesList',
        element: uploadZonesListElement,
      },
      {
        path: 'vendor-management/upload-ranking-list',
        element: uploadRankingListElement,
      },
      {
        path: 'VendorManagement/UploadRankingList',
        element: uploadRankingListElement,
      },
      {
        path: 'vendor-management/rank-file-management',
        element: rankFileManagementElement,
      },
      {
        path: 'VendorManagement/RankFileManagement',
        element: rankFileManagementElement,
      },
      {
        path: 'vendor-management/vendor-regions',
        element: vendorRegionsElement,
      },
      {
        path: 'VendorManagement/VendorRegions',
        element: vendorRegionsElement,
      },
      {
        path: 'vendor-management/company-management',
        element: companyManagementElement,
      },
      {
        path: 'VendorManagement/CompanyManagement',
        element: companyManagementElement,
      },
      {
        path: 'vendor-management/company-maintenance',
        element: companyMaintenanceElement,
      },
      {
        path: 'VendorManagement/CompanyMaintenance',
        element: companyMaintenanceElement,
      },
      {
        path: 'vendor-management/resource-maintenance',
        element: resourceMaintenanceElement,
      },
      {
        path: 'VendorManagement/ResourceMaintenance',
        element: resourceMaintenanceElement,
      },
      {
        path: 'vendor-management/view-resource',
        element: viewResourceElement,
      },
      {
        path: 'VendorManagement/ViewResource',
        element: viewResourceElement,
      },
      {
        path: 'vendor-management/edit-background-check',
        element: editBackgroundCheckElement,
      },
      {
        path: 'VendorManagement/EditBackgroundCheck',
        element: editBackgroundCheckElement,
      },
      {
        path: '/vendor-management/edit-background-check',
        element: editBackgroundCheckElement,
      },
      {
        path: '/VendorManagement/EditBackgroundCheck',
        element: editBackgroundCheckElement,
      },
      {
        path: 'vendor-management/set-company-admin',
        element: setCompanyAdminElement,
      },
      {
        path: 'VendorManagement/SetCompanyAdmin',
        element: setCompanyAdminElement,
      },
      {
        path: 'vendor-management/company-details/modify',
        element: companyDetailsElement,
      },
      {
        path: 'vendor-management/company-details/modify/:companyId',
        element: companyDetailsElement,
      },
      {
        path: 'VendorManagement/CompanyDetails/Modify',
        element: companyDetailsElement,
      },
      {
        path: 'VendorManagement/CompanyDetails/Modify/:companyId',
        element: companyDetailsElement,
      },
      {
        path: 'vendor-management/documents/:companyId',
        element: documentViewerElement,
      },
      {
        path: 'VendorManagement/ViewDocuments/:companyId',
        element: documentViewerElement,
      },
      // VendorPayments routes
      {
        path: 'VendorPayments/AddPrePayment',
        element: addPrePaymentElement,
      },
      {
        path: 'VendorPayments/PrePaymentList',
        element: prePaymentListElement,
      },
      {
        path: 'VendorPayments/AddVendorPenalty',
        element: addVendorPenaltyElement,
      },
      {
        path: 'VendorPayments/VendorPenaltyList',
        element: vendorPenaltyListElement,
      },
      // Location routes
      {
        path: 'Location/vendorview',
        element: vendorViewElement,
      },
      {
        path: 'Location/geomap',
        element: geomapElement,
      },
      // Disputes routes
      {
        path: 'Disputes/DisputeSummary',
        element: disputeSummaryElement,
      },
      {
        path: 'Disputes/AddDispute',
        element: addDisputeElement,
      },
      {
        path: 'Disputes/EditDispute',
        element: editDisputeElement,
      },
      // AdvancedSearch routes
      {
        path: 'AdvancedSearch',
        element: advancedSearchElement,
      },
      // CoreScript (SafeView iframe) routes
      {
        path: 'AddText2/:orderNumber',
        element: addText2Element,
      },
      {
        path: 'SACC2/:orderNumber',
        element: sacc2Element,
      },
      {
        // V2 SafeView iframe — master config.route.js line 105.
        // safeview: /nonaudit?context=ReconveyRequest&key=Property.PropertyLoanId&id={G_PROPERTY.id}
        path: 'Property/ReconveyRequest2',
        element: (
          <Suspense fallback={<UiLoader />}>
            <ReconveyRequest2Route />
          </Suspense>
        ),
      },
      {
        path: 'Property/record',
        element: (
          <Suspense fallback={<UiLoader />}>
            <PropertyRecordRoute />
          </Suspense>
        ),
      },
      {
        path: 'Property/record/:dashboardModule',
        element: (
          <Suspense fallback={<UiLoader />}>
            <PropertyRecordRoute />
          </Suspense>
        ),
      },
      {
        path: 'Property/MergeIds',
        element: (
          <Suspense fallback={<UiLoader />}>
            <MergeIdsRoute />
          </Suspense>
        ),
      },
      {
        path: 'Property/ServiceRelease',
        element: (
          <Suspense fallback={<UiLoader />}>
            <ServiceReleaseRoute />
          </Suspense>
        ),
      },
      {
        path: 'Property/costtodate',
        element: (
          <Suspense fallback={<UiLoader />}>
            <CostToDateRoute />
          </Suspense>
        ),
      },
      {
        path: 'Property/CTDDetails',
        element: (
          <Suspense fallback={<UiLoader />}>
            <CTDDetailsRoute />
          </Suspense>
        ),
      },
      {
        path: 'LoanQualifier',
        element: (
          <Suspense fallback={<UiLoader />}>
            <LoanQualifierRoute />
          </Suspense>
        ),
      },
      {
        path: 'Bids/approvaldetail/:id',
        element: (
          <Suspense fallback={<UiLoader />}>
            <ApprovalDetailRoute />
          </Suspense>
        ),
      },
      {
        path: 'Bids/auditscrub',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AuditScrubRoute />
          </Suspense>
        ),
      },
      {
        path: 'Bids/taskdetail/:id',
        element: (
          <Suspense fallback={<UiLoader />}>
            <TaskDetailRoute />
          </Suspense>
        ),
      },
      {
        path: 'Bids/approvaldisposition/:id',
        element: (
          <Suspense fallback={<UiLoader />}>
            <ApprovalDispositionRoute />
          </Suspense>
        ),
      },
      {
        path: 'Bids/tasksummary',
        element: (
          <Suspense fallback={<UiLoader />}>
            <TaskSummaryRoute />
          </Suspense>
        ),
      },
      {
        path: 'Bids/summary',
        element: (
          <Suspense fallback={<UiLoader />}>
            <BidsSummaryRoute />
          </Suspense>
        ),
      },
      {
        path: 'Bids/history',
        element: (
          <Suspense fallback={<UiLoader />}>
            <BidsHistoryRoute />
          </Suspense>
        ),
      },
      {
        path: 'Bids/addbid',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AddBidRoute />
          </Suspense>
        ),
      },
      {
        path: 'Bids/detail/:bidId',
        element: (
          <Suspense fallback={<UiLoader />}>
            <BidDetailRoute />
          </Suspense>
        ),
      },
      {
        path: 'Bids/disposition/:bidId',
        element: (
          <Suspense fallback={<UiLoader />}>
            <BidDispositionRoute />
          </Suspense>
        ),
      },
      {
        path: 'Damages/history',
        element: (
          <Suspense fallback={<UiLoader />}>
            <DamagesHistoryRoute />
          </Suspense>
        ),
      },
      {
        path: 'Damages/summary',
        element: (
          <Suspense fallback={<UiLoader />}>
            <DamagesSummaryRoute />
          </Suspense>
        ),
      },
      {
        path: 'Damages/summary_inspection',
        element: (
          <Suspense fallback={<UiLoader />}>
            <DamagesSummaryInspectionRoute />
          </Suspense>
        ),
      },
      {
        path: 'Damages/historysmall/:damageId',
        element: (
          <Suspense fallback={<UiLoader />}>
            <DamagesHistorySmallRoute />
          </Suspense>
        ),
      },
      {
        path: 'Damages/details/:damageId',
        element: (
          <Suspense fallback={<UiLoader />}>
            <DamagesDetailsRoute />
          </Suspense>
        ),
      },
      {
        path: 'MoreInformation/:name',
        element: (
          <Suspense fallback={<UiLoader />}>
            <MoreInformationRoute />
          </Suspense>
        ),
      },
      {
        path: 'Notes/:parent?',
        element: (
          <Suspense fallback={<UiLoader />}>
            <TaskActivityRoute />
          </Suspense>
        ),
      },
      {
        path: 'Audit/auditdashboard/workOrderFinancialWorkFlowHistory',
        element: (
          <Suspense fallback={<UiLoader />}>
            <WorkOrderFinancialWorkFlowHistoryRoute />
          </Suspense>
        ),
      },
      {
        path: 'Audit/auditdashboard/workOrderWorkFlowHistory',
        element: (
          <Suspense fallback={<UiLoader />}>
            <WorkOrderWorkFlowHistoryRoute />
          </Suspense>
        ),
      },
      {
        path: 'Audit/auditdashboard/results/additionalOrders',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AdditionalOrdersRoute />
          </Suspense>
        ),
      },
      {
        path: 'Audit/noChargePaytask',
        element: (
          <Suspense fallback={<UiLoader />}>
            <NoChargePayTaskRoute />
          </Suspense>
        ),
      },
      {
        path: 'Audit/auditdashboard/orderStatusTracker',
        element: (
          <OrderStatusTrackerRedirect>
            <Suspense fallback={<UiLoader />}>
              <OrderStatusTrackerRoute />
            </Suspense>
          </OrderStatusTrackerRedirect>
        ),
      },
      {
        path: 'Audit/addTask',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AddTaskRoute />
          </Suspense>
        ),
      },
      {
        path: 'Audit/auditdashboard/overview',
        element: (
          <Suspense fallback={<UiLoader />}>
            <OverviewRoute />
          </Suspense>
        ),
      },
      {
        path: 'Audit/auditdashboard/workOrderDashboard',
        element: (
          <Suspense fallback={<UiLoader />}>
            <WorkOrderDashboardRoute />
          </Suspense>
        ),
      },
      {
        path: 'Audit/auditdashboard/results',
        element: (
          <Suspense fallback={<UiLoader />}>
            <ResultsRoute />
          </Suspense>
        ),
      },
      {
        path: 'CaseManagement',
        element: <Navigate to='/CaseManagement/MyDashboard' replace />,
      },
      {
        path: 'CaseManagement/MyDashboard',
        element: withCaseManagementStyles(
          <Suspense fallback={<UiLoader />}>
            <ComplaintResolutionCenterRoute />
          </Suspense>
        ),
      },
      {
        path: 'CaseManagement/Intake',
        element: (
          <ProtectedRoute requiredPermissions={['canAccessComplaintIntake']}>
            <Navigate to='/CaseManagement/Complaint' replace />
          </ProtectedRoute>
        ),
      },
      {
        path: 'CaseManagement/Complaint',
        element: (
          <ProtectedRoute requiredPermissions={['canAccessComplaintIntake']}>
            {withCaseManagementStyles(
              <Suspense fallback={<UiLoader />}>
                <NewCaseSession>
                  <ComplaintIntakeRoute />
                </NewCaseSession>
              </Suspense>
            )}
          </ProtectedRoute>
        ),
      },
      {
        path: 'CaseManagement/Complaint/:id',
        element: withCaseManagementStyles(
          <Suspense fallback={<UiLoader />}>
            <ComplaintIntakeRoute />
          </Suspense>
        ),
      },
      {
        path: 'CaseManagement/ManagerOverview',
        element: (
          <ProtectedRoute requiredPermissions={['canAccessComplaintManagerOverview']}>
            {withCaseManagementStyles(
              <Suspense fallback={<UiLoader />}>
                <ComplaintManagerOverviewRoute />
              </Suspense>
            )}
          </ProtectedRoute>
        ),
      },
      {
        path: 'CaseManagement/ManagerConfiguration',
        element: (
          <ProtectedRoute requiredPermissions={['canManageComplaintRoutingRules']}>
            {withCaseManagementStyles(
              <Suspense fallback={<UiLoader />}>
                <ComplaintManagerOverviewRoute />
              </Suspense>
            )}
          </ProtectedRoute>
        ),
      },
      {
        path: 'CaseManagement/case/new',
        element: (
          <ProtectedRoute requiredPermissions={['canAccessComplaintIntake']}>
            {withCaseManagementStyles(
              <Suspense fallback={<UiLoader />}>
                <NewCaseSession>
                  <GenericCaseRoute />
                </NewCaseSession>
              </Suspense>
            )}
          </ProtectedRoute>
        ),
      },
      {
        path: 'CaseManagement/case/:caseId',
        element: withCaseManagementStyles(
          <Suspense fallback={<UiLoader />}>
            <GenericCaseRoute />
          </Suspense>
        ),
      },
      {
        // OrderEntry2 with itemTypeRecordId param (existing audit/results path).
        path: 'OrderEntry2/:itemTypeRecordId',
        element: (
          <Suspense fallback={<UiLoader />}>
            <OrderEntry2Route />
          </Suspense>
        ),
      },
      {
        path: 'OrderEntry2',
        element: (
          <Suspense fallback={<UiLoader />}>
            <OrderEntry2Route />
          </Suspense>
        ),
      },
      // V2 SafeView iframe routes — match master config.route.js exactly.
      // The URL stays as /...2/:id (no redirect to V1) and the iframe renders
      // the SafeView form. Replaces the prior `<Navigate>` stubs.
      {
        path: 'ICCConfirmation2/:orderNumber',
        element: (
          <Suspense fallback={<UiLoader />}>
            <ICCConfirmation2Route />
          </Suspense>
        ),
      },
      {
        path: 'ICCStatus2/:orderNumber',
        element: (
          <Suspense fallback={<UiLoader />}>
            <ICCStatus2Route />
          </Suspense>
        ),
      },
      {
        path: 'CodeViolationRequest2/:orderNumber',
        element: (
          <Suspense fallback={<UiLoader />}>
            <CodeViolationRequest2Route />
          </Suspense>
        ),
      },
      {
        // Legacy /AuditRequest2/:orderNumber — SafeView iframe route.
        // Mirrors config.route.js:754 (context=AuditRequest&key=WorkOrder.OrderNumber).
        // Reached from OrderStatusTrackerRoute "New Core Script Submit Results" button.
        path: 'AuditRequest2/:orderNumber',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AuditRequest2Route />
          </Suspense>
        ),
      },
      {
        // VendorReassign2 — SafeView iframe matching master config.route.js:654.
        // Context: VendorReassignRequest. Reached from WorkOrderDashboardRoute
        // "Reassign Vendor Corescript" button.
        path: 'VendorReassign2/:orderNumber',
        element: (
          <Suspense fallback={<UiLoader />}>
            <VendorReassign2Route />
          </Suspense>
        ),
      },
      {
        // Schedule2 — SafeView iframe matching master config.route.js:698.
        // Context: ScheduleScriptRequest. V1 counterpart at /Schedule/:orderNumber
        // is rendered via the React-native CoreScript engine.
        path: 'Schedule2/:orderNumber',
        element: (
          <Suspense fallback={<UiLoader />}>
            <Schedule2Route />
          </Suspense>
        ),
      },
      {
        // AddVendorText2 — SafeView iframe matching master config.route.js:784.
        // Context: VendorTextRequest, key=ServiceAreaId. Reached from the
        // "Add Text Core Script" subnav link in the serviceArea context.
        path: 'AddVendorText2/:serviceAreaId',
        element: (
          <Suspense fallback={<UiLoader />}>
            <AddVendorText2Route />
          </Suspense>
        ),
      },
      // V2 SafeView iframe routes (URL stays /...2/, content is the SafeView form).
      // Each mirrors master config.route.js exactly — see the route component file
      // for the matching SafeView context.
      {
        path: 'OrderModificationRequest2/:workOrderNumber',
        element: (
          <Suspense fallback={<UiLoader />}>
            <OrderModificationRequest2Route />
          </Suspense>
        ),
      },
      {
        path: 'PostAuditRequest2/:orderNumber',
        element: (
          <Suspense fallback={<UiLoader />}>
            <PostAuditRequest2Route />
          </Suspense>
        ),
      },
      {
        path: 'Property/ConveyImpediments2',
        element: (
          <Suspense fallback={<UiLoader />}>
            <ConveyImpediments2Route />
          </Suspense>
        ),
      },
      {
        path: 'CreateProperty2',
        element: (
          <Suspense fallback={<UiLoader />}>
            <CreateProperty2Route />
          </Suspense>
        ),
      },
      {
        path: 'vendor-connect',
        element: (
          <Suspense fallback={<UiLoader />}>
            <VendorAuthGuard>
              <VendorConnectRoute />
            </VendorAuthGuard>
          </Suspense>
        ),
      },
      {
        path: 'vendor-connect/orders/:orderNumber',
        element: (
          <Suspense fallback={<UiLoader />}>
            <VendorAuthGuard>
              <VendorConnectOrderDetailsRoute />
            </VendorAuthGuard>
          </Suspense>
        ),
      },
      {
        // AngularJS hash route compatibility: /#/VendorConnect/Dashboard → vendor-connect
        path: 'VendorConnect/Dashboard',
        element: <Navigate to='/vendor-connect' replace />,
      },
      {
        path: 'vendor-connect/callback',
        element: (
          <Suspense fallback={<UiLoader />}>
            <Auth0CallbackHandler />
          </Suspense>
        ),
      },
      {
        path: '/VendorManagement/CreateResource',
        element: (
          <ProtectedRoute>
            <CreateResourceRoute />
          </ProtectedRoute>
        ),
      },
      {
        path: 'reports/overview',
        element: <ReportsOverviewRoute />,
      },
      {
        path: 'forbidden',
        element: <AccessDeniedRoute />,
      },
      {
        path: '*',
        element: <NotFoundRoute />,
      },
    ],
  },
];

function normalizeInitialPath(path?: string) {
  if (!path) {
    return '/home';
  }

  const hashIndex = path.indexOf('#');
  if (hashIndex >= 0) {
    return normalizeInitialPath(path.substring(hashIndex + 1));
  }

  if (!path.startsWith('/')) {
    return `/${path.replace(/^\/+/, '')}`;
  }

  return path;
}

function RootRouter() {
  const mountConfig = getMountConfig();

  const router = useMemo(() => {
    if (mountConfig.mode === 'embedded') {
      const initialEntry = normalizeInitialPath(mountConfig.initialPath);
      return sentryCreateMemoryRouter(routeConfig, {
        initialEntries: [initialEntry],
        initialIndex: 0,
      });
    }

    // Standalone: use hash router so URLs match AngularJS format (/#/path)
    return sentryCreateHashRouter(routeConfig);
  }, [mountConfig.initialPath, mountConfig.mode]);

  return (
    <Suspense fallback={<UiLoader />}>
      <RouterProvider router={router} future={{ v7_startTransition: true }} />
    </Suspense>
  );
}

export default RootRouter;
